import { useState } from 'react';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import Icon from '../../../../components/Icon/Icon';
import { CustomDateRangePicker } from '../../../../components/DateRangePicker';

const WhatsAppBillingFilters = ({ filters, onFilterChange, onApplyFilter, loading }) => {
  const countries = [
    { value: 'MY', label: 'Malaysia', flag: '🇲🇾' },
    { value: 'ID', label: 'Indonesia', flag: '🇮🇩' },
    { value: 'SG', label: 'Singapore', flag: '🇸🇬' },
    { value: 'TH', label: 'Thailand', flag: '🇹🇭' },
    { value: 'VN', label: 'Vietnam', flag: '🇻🇳' },
    { value: 'PH', label: 'Philippines', flag: '🇵🇭' }
  ];

  // Convert current filter values to react-select format
  const selectedCountries = Array.isArray(filters.country) 
    ? countries.filter(country => filters.country.includes(country.value))
    : countries.filter(country => country.value === filters.country);

  const handleCountryChange = (selectedOptions) => {
    const countryValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    onFilterChange({ country: countryValues });
  };

  const handleDateChange = (field, value) => {
    onFilterChange({ [field]: value });
  };

  // Custom styles for React-Select to match existing design
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      border: '1px solid #D0D5DD',
      borderRadius: '4px',
      boxShadow: state.isFocused ? '0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0 0 0 1px #0BA9E0' : '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      '&:hover': {
        borderColor: '#D0D5DD'
      },
      fontFamily: 'Poppins',
      fontSize: '13px',
      minWidth: '200px'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#DCF0FC',
      borderRadius: '8px',
      padding: '2px 6px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontFamily: 'Poppins',
      fontWeight: '400',
      fontSize: '13px',
      color: '#344054'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0BA9E0',
      '&:hover': {
        backgroundColor: '#0BA9E0',
        color: 'white'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000,
      border: '1px solid #D0D5DD',
      borderRadius: '4px',
      boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: 'Poppins',
      fontSize: '13px',
      color: '#344054',
      backgroundColor: state.isSelected ? '#F1F6FF' : state.isFocused ? '#F9FAFB' : 'transparent',
      '&:hover': {
        backgroundColor: '#F9FAFB'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      fontFamily: 'Poppins',
      fontSize: '13px',
      color: '#667085'
    })
  };

  // Custom option component to display flag + country name
  const CustomOption = ({ innerRef, innerProps, data, isSelected, isFocused }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#F1F6FF' : isFocused ? '#F9FAFB' : 'transparent',
        borderBottom: '1px solid #F2F4F7'
      }}
    >
      <span style={{ fontSize: '16px' }}>{data.flag}</span>
      <span style={{
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '13px',
        lineHeight: '1.5em',
        color: '#344054'
      }}>
        {data.label}
      </span>
    </div>
  );

  // Custom multi-value component to display flag + country name
  const CustomMultiValue = ({ data, removeProps }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 6px',
      backgroundColor: '#DCF0FC',
      borderRadius: '8px',
      margin: '2px'
    }}>
      <span style={{ fontSize: '14px' }}>{data.flag}</span>
      <span style={{
        fontFamily: 'Poppins',
        fontWeight: '400',
        fontSize: '13px',
        color: '#344054'
      }}>
        {data.label}
      </span>
      <div
        {...removeProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          marginLeft: '4px',
          color: '#0BA9E0'
        }}
      >
        <Icon
          name="cancel"
          color="#0BA9E0"
          width={14}
          height={14}
        />
      </div>
    </div>
  );

  const renderCountryDropdown = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'stretch',
      gap: '6px',
      flex: 1
    }}>
      <label style={{
        fontFamily: 'Poppins',
        fontWeight: '500',
        fontSize: '14px',
        lineHeight: '1.5em',
        color: '#344054'
      }}>
        Country
      </label>
      <Select
        isMulti
        value={selectedCountries}
        onChange={handleCountryChange}
        options={countries}
        styles={customSelectStyles}
        components={{
          Option: CustomOption,
          MultiValue: CustomMultiValue
        }}
        placeholder="Select countries..."
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isSearchable
      />
    </div>
  );

  const renderDateRangePicker = () => {
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    const handleDateRangeChange = (dates) => {
      const [start, end] = dates;
      if (start && end) {
        onFilterChange({
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0]
        });
      }
    };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        position: 'relative'
      }}>
        <CustomDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
          className="billing-date-picker"
        />
      </div>
    );
  };

  const renderApplyFilterButton = () => (
    <Button
      onClick={onApplyFilter}
      disabled={loading}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        backgroundColor: '#0BA9E0',
        border: 'none',
        borderRadius: '4px',
        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
        fontFamily: 'Poppins',
        fontWeight: '500',
        fontSize: '13px',
        lineHeight: '1.5em',
        color: '#FFFFFF'
      }}
    >
      Apply Filter
    </Button>
  );

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-end',
      alignSelf: 'stretch',
      gap: '10px',
      flex: 1
    }}>
      {renderCountryDropdown()}
      {renderDateRangePicker()}
      {renderApplyFilterButton()}
    </div>
  );
};

export default WhatsAppBillingFilters;